const ALLOWED_TAGS = new Set(['SPAN', 'P', 'BR', 'EM']);
const ALLOWED_CLASSES = new Set(['home-valor__title-accent', 'home-valor__copy-accent']);

const VALOR_MARKUP_PATTERN = /<(span|p|br|em)\b/i;

export function escapeValorHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function containsValorMarkup(text: string): boolean {
  return VALOR_MARKUP_PATTERN.test(text);
}

/** Sanitiza HTML permitido para textos administrables de valor (B.3). */
export function sanitizeValorHtml(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }

  if (!containsValorMarkup(trimmed)) {
    return escapeValorHtml(trimmed);
  }

  if (typeof DOMParser === 'undefined') {
    return escapeValorHtml(trimmed);
  }

  const doc = new DOMParser().parseFromString(trimmed, 'text/html');
  return serializeAllowedValorNodes(doc.body).trim();
}

function serializeAllowedValorNodes(parent: ParentNode): string {
  let html = '';

  parent.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      html += escapeValorHtml(node.textContent ?? '');
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node as HTMLElement;
    const tag = element.tagName;

    if (!ALLOWED_TAGS.has(tag)) {
      html += serializeAllowedValorNodes(element);
      return;
    }

    if (tag === 'BR') {
      html += '<br>';
      return;
    }

    const classes = [...element.classList].filter((className) =>
      ALLOWED_CLASSES.has(className)
    );
    const classAttr = classes.length ? ` class="${classes.join(' ')}"` : '';
    const tagName = tag.toLowerCase();

    html += `<${tagName}${classAttr}>${serializeAllowedValorNodes(element)}</${tagName}>`;
  });

  return html;
}

export function plainTextToValorTitleHtml(raw: string): string {
  const text = escapeValorHtml(raw.trim());
  if (!text) {
    return '';
  }
  return text;
}

export function plainTextToValorIntroHtml(raw: string): string {
  const text = raw.trim();
  if (!text) {
    return '';
  }

  const paragraphs = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  if (paragraphs.length <= 1) {
    return `<p>${escapeValorHtml(text)}</p>`;
  }

  return paragraphs.map((p) => `<p>${escapeValorHtml(p)}</p>`).join('');
}

export function prepareValorTitleHtml(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }

  if (containsValorMarkup(trimmed)) {
    return sanitizeValorHtml(trimmed);
  }

  return plainTextToValorTitleHtml(trimmed);
}

export function prepareValorIntroHtml(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }

  if (containsValorMarkup(trimmed)) {
    return sanitizeValorHtml(trimmed);
  }

  return plainTextToValorIntroHtml(trimmed);
}
